import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../../utils/api";
import Linkify from "linkify-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/Context/AuthContext";
const COLORS = {
  gold: "#FFD700",
  black: "#0b0b0b",
  blackAlt: "#111111",
  white: "#ffffff",
  grayLine: "rgba(255,255,255,0.12)",
  grayText: "rgba(255,255,255,0.85)",
  mutedText: "rgba(255,255,255,0.65)",
  link: "#FFD700",
  error: "#ef4444",
};

const FORUM_URL = "/Chat-Thread";

const linkifyOptions = {
  target: "_blank",
  rel: "noopener noreferrer",
  render: ({ attributes, content }) => (
    <a
      {...attributes}
      style={{
        color: COLORS.gold,
        textDecoration: "underline",
        fontWeight: 500,
      }}
    >
      {content}{" "}
    </a>
  ),
};

function getCurrentPath() {
  if (typeof window === "undefined") return "/";
  return window.location.pathname || "/";
}

function getRouteConfig(pathname) {
  const path = pathname.toLowerCase();

  if (path.startsWith("/grants-dashboard")) {
    return {
      assistantName: "Grant Assistant",
      launcherLabel: "Grant Help",
      teaser: "Need help with grant updates or receipts?",
      intro:
        "Hi! I can help you log digitized documents, understand your dashboard, and find where to upload receipts.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "How to log new documents",
          q: "How do I log newly digitized documents on the grants dashboard?",
        },
        {
          label: "Upload receipts",
          q: "Where do I upload grant receipts?",
        },
        {
          label: "Quarter totals",
          q: "What can I see on the grants dashboard?",
        },
        {
          label: "Login help",
          q: "How do I access my grants dashboard?",
        },
      ],
    };
  }

  if (path.startsWith("/grants")) {
    return {
      assistantName: "Grant Assistant",
      launcherLabel: "Grant Help",
      teaser: "Looking for grant amounts or deadlines?",
      intro:
        "Hi! I can help with grant amounts, deadlines, eligibility, recipient reporting, and dashboard questions.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "Grant amounts",
          q: "What grant amounts are available?",
        },
        {
          label: "Grant deadlines",
          q: "What are the grant deadlines?",
        },
        {
          label: "Recipient dashboard help",
          q: "How do grant recipients log digitized documents and upload receipts?",
        },
        {
          label: "Eligibility",
          q: "Who can apply for grants?",
        },
      ],
    };
  }

  if (
    path.startsWith("/summer-workshop-2026") ||
    path.startsWith("/about-summer-workshop-2026")
  ) {
    return {
      assistantName: "Workshop Assistant",
      launcherLabel: "Workshop Help",
      teaser: "Need help with Summer Workshop 2026?",
      intro:
        "Hi! I can help you learn about the Summer Residency 2026 participants, their institutions, research interests, and the digital humanities projects they developed during the program.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "Meet participants",
          q: "Who participated in the Summer Residency 2026?",
        },
        {
          label: "Digital projects",
          q: "Show me the digital humanities projects created during the Summer Residency 2026.",
        },
      ],
    };
  } // SUMMER RESIDENCY 2027
  if (
    path.startsWith("/summer-workshop-2027") ||
    path.startsWith("/about-summer-workshop-2027")
  ) {
    return {
      assistantName: "Residency Assistant",
      launcherLabel: "Residency Help",
      teaser: "Questions about Summer Residency 2027?",
      intro:
        "Hi! I can help with information currently available about the Summer Residency 2027 for secondary school educators.",
      helperText:
        "Answers are based on currently published MS Digital Hub information. Additional 2027 program details will be added as they become available.",
      quickTopics: [
        {
          label: "2027 Residency overview",
          q: "What is the Summer Residency 2027?",
        },
        {
          label: "Who is it for?",
          q: "Who is the Summer Residency 2027 intended for?",
        },
        {
          label: "Application information",
          q: "What application information is currently available for the Summer Residency 2027?",
        },
        {
          label: "Program updates",
          q: "What information is currently available about the Summer Residency 2027?",
        },
      ],
    };
  } // 2027 CONFERENCE
  if (path.startsWith("/conference")) {
    return {
      assistantName: "Conference Assistant",
      launcherLabel: "Conference Help",
      teaser: "Questions about the 2027 conference?",
      intro:
        "Hi! I can help with information about Mississippi’s Digital Present, Mississippi’s Digital Future, including conference dates, topics, submission guidelines, and important deadlines.",
      helperText:
        "Answers are based on conference information published by MS Digital Hub.",
      quickTopics: [
        {
          label: "Conference overview",
          q: "What is Mississippi’s Digital Present, Mississippi’s Digital Future?",
        },
        {
          label: "Important dates",
          q: "What are the important dates and deadlines for the 2027 conference?",
        },
        {
          label: "Conference topics",
          q: "What topics are being accepted for the conference?",
        },
        {
          label: "Submission guidelines",
          q: "What are the submission guidelines for the 2027 conference?",
        },
      ],
    };
  }

  if (
    path.startsWith("/summer-workshop-2025") ||
    path.startsWith("/about-summer-workshop-2025") ||
    path.startsWith("/about-summer-workshop-program") ||
    path.startsWith("/summerworkshop-2025-archive")
  ) {
    return {
      assistantName: "Workshop Assistant",
      launcherLabel: "Workshop Help",
      teaser: "Need help finding workshop information?",
      intro:
        "Hi! I can help with workshop schedules, materials, program details, and related pages.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "Workshop schedule",
          q: "What is the workshop schedule?",
        },
        {
          label: "Program overview",
          q: "Give me an overview of the Summer Residency program.",
        },
        {
          label: "Workshop materials",
          q: "Where can I find workshop materials?",
        },
        {
          label: "FAQs",
          q: "Where can I find the workshop FAQs?",
        },
      ],
    };
  }

  if (
    path.startsWith("/transcription") ||
    path.startsWith("/get-started-transcription") ||
    path.startsWith("/transcription-instructions")
  ) {
    return {
      assistantName: "Transcription Helper",
      launcherLabel: "Transcription Help",
      teaser: "Need help getting started with transcription?",
      intro:
        "Hi! I can help you get started with transcription, find instructions, explain the review workflow, and point you to relevant pages.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "How to start",
          q: "How do I start transcribing?",
        },
        {
          label: "Instructions",
          q: "Where can I find transcription instructions?",
        },
        {
          label: "Review workflow",
          q: "How does transcription review work?",
        },
        {
          label: "Find documents",
          q: "How do I find documents to transcribe?",
        },
      ],
    };
  }

  if (path.startsWith("/dh-tutorials")) {
    return {
      assistantName: "Tutorial Guide",
      launcherLabel: "Tutorial Help",
      teaser: "Looking for DH tutorials?",
      intro:
        "Hi! I can help you find available digital humanities tutorials and guide you to the right place to start.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "List tutorials",
          q: "List the available digital humanities tutorials.",
        },
        {
          label: "Beginner-friendly options",
          q: "Which tutorials are beginner-friendly?",
        },
        {
          label: "Getting started",
          q: "Where should I start with the DH tutorials?",
        },
        {
          label: "Tutorial page help",
          q: "What can I find on the DH tutorials page?",
        },
      ],
    };
  }

  if (path.startsWith("/contact-us")) {
    return {
      assistantName: "Site Assistant",
      launcherLabel: "Get Help",
      teaser: "Need contact information?",
      intro:
        "Hi! I can help you find contact details, support pages, and general information across MS Digital Hub.",
      helperText:
        "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
      quickTopics: [
        {
          label: "Contact details",
          q: "Give me contact details.",
        },
        {
          label: "General support",
          q: "How can I get help on MS Digital Hub?",
        },
        {
          label: "Workshop contact",
          q: "How do I contact the team about workshops?",
        },
        {
          label: "Grant contact",
          q: "How do I contact the team about grants?",
        },
      ],
    };
  }
  if (path.startsWith("/transcribeathon-events/")) {
    return {
      assistantName: "Participant Helper",
      launcherLabel: "Event Help",
      teaser: "Need help with scoring or leaderboard?",
      intro:
        "Hi! I can help registered participants understand scoring, rubric, leaderboard ranking, and what counts as a meaningful contribution during the Transcribe-athon event.",
      helperText: "Answers are tailored for participants on this event page.",
      quickTopics: [
        {
          label: "Scoring rubric",
          q: "How scoring works",
        },
        {
          label: "Transcription points",
          q: "How many points do I get for transcribing a page?",
        },
        {
          label: "Review points",
          q: "How many points do I get for reviewing a page?",
        },
        {
          label: "Fast work",
          q: "How does fast work affect points?",
        },
        {
          label: "Leaderboard ranking",
          q: "How is the leaderboard ranked?",
        },
        {
          label: "Meaningful contribution",
          q: "What counts as a meaningful contribution?",
        },
      ],
    };
  }

  return {
    assistantName: "Hub Assistant",
    launcherLabel: "Ask the Hub",
    teaser: "Need help finding something on this page?",
    intro:
      "Hi! I can help you find grants, workshop information, DH tutorials, transcription guidance, and contact details.",
    helperText:
      "Answers based on MS Digital Hub site content. If you still need help, you can post in the Hub Discussion Forum.",
    quickTopics: [
      /* {
        label: "Grant amounts & deadlines",
        q: "What grant amounts are available and what are the deadlines?",
      },*/
      {
        label: "Summer Residency overview",
        q: "Give me an overview of the Summer Residency program.",
      },
      {
        label: "2026 Participants",
        q: "Who participated in the Summer Residency 2026 and what digital humanities projects did they create?",
      },
      /*  {
        label: "Workshop schedule",
        q: "What is the workshop schedule?",
      },*/
      {
        label: "Transcription Help",
        q: "How do I start transcribing?",
      },
      {
        label: "DH tutorials",
        q: "List the available digital humanities tutorials.",
      },
    ],
  };
}

function trackChatEvent(eventName, payload = {}) {
  try {
    console.log("[chat-analytics]", eventName, payload);

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, payload);
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("hub-chat-event", {
          detail: { eventName, payload },
        })
      );
    }
  } catch (err) {
    console.warn("trackChatEvent error:", err);
  }
}

function sanitizeAnswerText(text) {
  if (!text) return "";
  return text.replace(/\n+\s*Sources?\s*:\s*([\s\S]*?)$/i, "").trim();
}

function prettifySourceLabel(url) {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, "") || "/";
    if (path === "/") return "Home";
    const last = path.split("/").filter(Boolean).pop() || "Source";
    return last.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  } catch {
    return "Source";
  }
}

export default function ChatWidgetPrototype() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const pathname = getCurrentPath();
  const routeConfig = useMemo(() => getRouteConfig(pathname), [pathname]);

  const storageKey = useMemo(
    () => `proto_chat_history_v3:${pathname}`,
    [pathname]
  );

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);
  const [guestThreadForm, setGuestThreadForm] = useState({
    name: "",
    email: "",
    title: "",
    content: "",
  });

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: routeConfig.intro,
    },
  ]);

  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, open]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      }
    } catch {}

    setMessages([{ role: "assistant", content: routeConfig.intro }]);
  }, [storageKey, routeConfig.intro]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {}
  }, [messages, storageKey]);

  useEffect(() => {
    if (open || teaserDismissed) return;

    const timer = setTimeout(() => {
      setShowTeaser(true);
      trackChatEvent("hub_chat_teaser_shown", { path: pathname });
    }, 4000);

    return () => clearTimeout(timer);
  }, [open, teaserDismissed, pathname]);

  useEffect(() => {
    if (!open) return;

    inputRef.current?.focus();

    if (!hasTrackedOpen) {
      trackChatEvent("hub_chat_opened", { path: pathname });
      setHasTrackedOpen(true);
    }
  }, [open, hasTrackedOpen, pathname]);

  useEffect(() => {
    const handler = () => openWidget("page_cta");
    window.addEventListener("open-hub-chat", handler);
    return () => window.removeEventListener("open-hub-chat", handler);
  }, []);

  const openWidget = (source = "launcher") => {
    setOpen(true);
    setShowTeaser(false);
    trackChatEvent("hub_chat_open_triggered", { path: pathname, source });
  };

  const closeWidget = () => {
    setOpen(false);
    trackChatEvent("hub_chat_minimized", { path: pathname });
  };

  const dismissTeaser = () => {
    setShowTeaser(false);
    setTeaserDismissed(true);
    trackChatEvent("hub_chat_teaser_dismissed", { path: pathname });
  };

  const clearConversation = () => {
    const fresh = [{ role: "assistant", content: routeConfig.intro }];
    setMessages(fresh);
    setError(null);

    try {
      localStorage.removeItem(storageKey);
    } catch {}

    trackChatEvent("hub_chat_cleared", { path: pathname });
  };

  const ask = async (maybeQ, source = "typed") => {
    const q = (maybeQ ?? question).trim();
    if (!q || loading) return;

    setError(null);
    setLoading(true);

    trackChatEvent("hub_chat_question_sent", {
      path: pathname,
      source,
      questionLength: q.length,
    });

    setMessages((prev) => [...prev, { role: "user", content: q }]);

    if (!maybeQ) setQuestion("");

    try {
      const res = await fetch(`${API_BASE_URL}/rag/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          question: q,
          pagePath: pathname,
        }),
      });

      if (!res.ok) {
        const body = await res.text();
        console.error("rag error", res.status, body);

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Sorry — something went wrong while fetching an answer. Try one of the suggested questions or try again in a moment. If you still need help, you can also post your question in the Hub Discussion Forum.",
          },
        ]);

        trackChatEvent("hub_chat_answer_failed", {
          path: pathname,
          status: res.status,
        });
      } else {
        const data = await res.json();
        const rawContent =
          typeof data?.answer === "string"
            ? data.answer
            : "Sorry, I couldn't generate an answer.";
        const content = sanitizeAnswerText(rawContent);
        const sources = Array.isArray(data?.sources) ? data.sources : [];

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content,
            sources,
          },
        ]);

        trackChatEvent("hub_chat_answer_received", {
          path: pathname,
          sourceCount: sources.length,
        });
      }
    } catch (e) {
      console.error(e);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Network error — please try again. You can also try one of the quick questions above. If you still don’t find what you need, post your question in the Hub Discussion Forum.",
        },
      ]);

      setError(e?.message || String(e));

      trackChatEvent("hub_chat_network_error", {
        path: pathname,
        message: e?.message || String(e),
      });
    } finally {
      setLoading(false);
    }
  };
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const canCreateSupportThread =
    guestThreadForm.name.trim() &&
    isValidEmail(guestThreadForm.email) &&
    guestThreadForm.title.trim() &&
    guestThreadForm.content.trim();
  const createGuestThread = async () => {
    try {
      if (
        !guestThreadForm.name ||
        !guestThreadForm.email ||
        !guestThreadForm.title ||
        !guestThreadForm.content
      ) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Please fill name, email, title, and details before creating the thread.",
          },
        ]);
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append("name", guestThreadForm.name);
      formData.append("email", guestThreadForm.email);
      formData.append("title", guestThreadForm.title);
      formData.append("content", guestThreadForm.content);

      const res = await fetch(`${API_BASE_URL}/threads/guest-create`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to create thread.");
      }

      setCreatingThread(false);

      setGuestThreadForm({
        name: "",
        email: "",
        title: "",
        content: "",
      });

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Your thread has been created successfully. I also emailed you a private link where you can track comments and updates.",
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            err.message || "Something went wrong while creating your thread.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };
  const fireQuickTopic = async (q, label) => {
    if (loading) return;
    trackChatEvent("hub_chat_quick_topic_clicked", {
      path: pathname,
      label,
    });
    ask(q, "quick_topic");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  const FAB = {
    bg: "#FFD700",
    fg: "#0b0b0b",
    border: "1px solid #FFD700",
    hoverBg: "#ffe34d",
    ring: "0 0 0 3px rgba(255,215,0,0.35)",
  };

  const ui = {
    fabContainer: {
      position: "fixed",
      right: 24,
      bottom: 24,
      zIndex: 10000,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 10,
    },
    teaserWrap: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      maxWidth: 280,
    },
    teaser: {
      background: "rgba(0,0,0,0.92)",
      color: "#fff",
      padding: "11px 13px",
      borderRadius: 14,
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1.3,
      border: "1px solid rgba(255,255,255,0.18)",
      boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
      cursor: "pointer",
    },
    teaserClose: {
      width: 28,
      height: 28,
      borderRadius: 999,
      border: "1px solid rgba(255,255,255,0.16)",
      background: "rgba(0,0,0,0.92)",
      color: "#fff",
      cursor: "pointer",
      fontSize: 14,
      lineHeight: 1,
    },
    launcherRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    pill: {
      background: "rgba(0,0,0,0.88)",
      color: "#fff",
      padding: "10px 12px",
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1.1,
      border: "1px solid rgba(255,255,255,0.18)",
      opacity: 0.96,
      userSelect: "none",
    },
    fab: {
      width: 62,
      height: 62,
      borderRadius: 999,
      background: FAB.bg,
      color: FAB.fg,
      border: FAB.border,
      cursor: "pointer",
      fontWeight: 900,
      fontSize: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition:
        "transform 120ms ease, box-shadow 120ms ease, background 140ms ease",
      animation: "hubPulse 2.4s ease-out infinite",
    },
    panel: {
      position: "fixed",
      right: 24,
      bottom: 24,
      width: 430,
      maxWidth: "94vw",
      height: 600,
      maxHeight: "84vh",
      background: COLORS.black,
      color: COLORS.white,
      borderRadius: 18,
      boxShadow: "0 22px 55px rgba(0,0,0,0.5)",
      overflow: "hidden",
      zIndex: 10001,
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${COLORS.grayLine}`,
    },
    header: {
      padding: "12px 14px",
      background: COLORS.blackAlt,
      borderBottom: `1px solid ${COLORS.grayLine}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    headerTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 800,
      fontSize: 14,
      letterSpacing: 0.2,
    },
    headerSub: {
      fontSize: 11,
      color: COLORS.mutedText,
    },
    dot: {
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: 999,
      background: COLORS.gold,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    miniBtn: {
      background: "transparent",
      color: COLORS.white,
      border: `1px solid ${COLORS.grayLine}`,
      borderRadius: 8,
      padding: "6px 8px",
      fontSize: 12,
      cursor: "pointer",
    },
    body: {
      padding: 12,
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0) 100%)",
    },
    introCard: {
      border: `1px solid ${COLORS.grayLine}`,
      borderRadius: 14,
      padding: "12px 13px",
      background: "rgba(255,255,255,0.03)",
    },
    introText: {
      fontSize: 13.5,
      lineHeight: 1.45,
      color: COLORS.grayText,
      whiteSpace: "pre-wrap",
    },
    helperText: {
      marginTop: 8,
      fontSize: 11.5,
      color: COLORS.mutedText,
    },
    quickSection: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      marginBottom: 4,
    },
    quickLabel: {
      fontSize: 12,
      color: COLORS.mutedText,
      fontWeight: 700,
      paddingLeft: 2,
    },
    quickGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    },
    quickBtn: {
      textAlign: "left",
      padding: "11px 12px",
      borderRadius: 12,
      background: "transparent",
      border: `1px solid rgba(255,215,0,0.5)`,
      color: COLORS.gold,
      fontSize: 12.5,
      cursor: "pointer",
      lineHeight: 1.25,
      fontWeight: 700,
    },
    forumCard: {
      border: `1px solid ${COLORS.grayLine}`,
      borderRadius: 14,
      padding: "12px 13px",
      background: "rgba(255,255,255,0.02)",
    },
    forumTitle: {
      fontSize: 12.5,
      fontWeight: 800,
      color: COLORS.white,
      marginBottom: 6,
    },
    forumText: {
      fontSize: 12.5,
      lineHeight: 1.45,
      color: COLORS.mutedText,
    },
    forumBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: 10,
      padding: "9px 12px",
      borderRadius: 10,
      background: "transparent",
      border: `1px solid rgba(255,215,0,0.5)`,
      color: COLORS.gold,
      textDecoration: "none",
      fontSize: 12.5,
      fontWeight: 800,
    },
    bubbleUser: {
      alignSelf: "flex-end",
      background: "#1a1a1a",
      color: COLORS.white,
      padding: "10px 12px",
      borderRadius: 14,
      maxWidth: "86%",
      margin: "2px 0",
      border: `1px solid ${COLORS.grayLine}`,
      whiteSpace: "pre-wrap",
    },
    bubbleAsst: {
      alignSelf: "flex-start",
      background: COLORS.blackAlt,
      color: COLORS.grayText,
      padding: "10px 12px",
      borderRadius: 14,
      maxWidth: "86%",
      margin: "2px 0",
      border: `1px solid ${COLORS.grayLine}`,
      whiteSpace: "pre-wrap",
    },
    msgMeta: {
      marginTop: 8,
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
    sourceBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 9px",
      borderRadius: 999,
      border: `1px solid rgba(255,215,0,0.35)`,
      color: COLORS.gold,
      textDecoration: "none",
      fontSize: 11.5,
      fontWeight: 700,
    },
    loadingBubble: {
      alignSelf: "flex-start",
      background: COLORS.blackAlt,
      color: COLORS.grayText,
      padding: "10px 12px",
      borderRadius: 14,
      border: `1px solid ${COLORS.grayLine}`,
      fontStyle: "italic",
    },
    errorBubble: {
      alignSelf: "flex-start",
      background: COLORS.blackAlt,
      color: COLORS.white,
      padding: "10px 12px",
      borderRadius: 14,
      border: `1px solid ${COLORS.error}`,
      fontSize: 12.5,
    },
    footer: {
      padding: 12,
      borderTop: `1px solid ${COLORS.grayLine}`,
      display: "flex",
      gap: 8,
      alignItems: "center",
      background: COLORS.blackAlt,
    },
    input: {
      flex: 1,
      background: "#0f0f0f",
      color: COLORS.white,
      border: `1px solid ${COLORS.grayLine}`,
      borderRadius: 10,
      padding: "10px 12px",
      outline: "none",
      fontSize: 14,
    },
    send: {
      background: COLORS.gold,
      color: COLORS.black,
      border: "none",
      borderRadius: 10,
      padding: "10px 14px",
      fontWeight: 800,
      cursor: "pointer",
    },
  };

  return (
    <>
      {" "}
      <style>{`
            @keyframes hubPulse {
               0% { box-shadow: 0 0 0 0 rgba(255,215,0,0.45); }
               70% { box-shadow: 0 0 0 14px rgba(255,215,0,0); }
               100% { box-shadow: 0 0 0 0 rgba(255,215,0,0); }
            }

            @media (max-width: 640px) {
               .hub-chat-panel {
                  right: 12px !important;
                  bottom: 12px !important;
                  width: calc(100vw - 24px) !important;
                  height: 76vh !important;
                  max-height: 76vh !important;
               }

               .hub-chat-fab-wrap {
                  right: 14px !important;
                  bottom: 14px !important;
               }

               .hub-chat-quick-grid {
                  grid-template-columns: 1fr !important;
               }
            }
         `}</style>{" "}
      {!open && (
        <div style={ui.fabContainer} className="hub-chat-fab-wrap">
          {" "}
          {showTeaser && (
            <div style={ui.teaserWrap}>
              {" "}
              <div
                style={ui.teaser}
                onClick={() => openWidget("teaser")}
                title={routeConfig.teaser}
              >
                {routeConfig.teaser}{" "}
              </div>{" "}
              <button
                type="button"
                style={ui.teaserClose}
                onClick={dismissTeaser}
                aria-label="Dismiss help prompt"
                title="Dismiss"
              >
                ×{" "}
              </button>{" "}
            </div>
          )}{" "}
          <div style={ui.launcherRow}>
            {" "}
            <div style={ui.pill}>
              {routeConfig.launcherLabel}{" "}
              <span style={{ opacity: 0.75 }}>(AI)</span>{" "}
            </div>{" "}
            <button
              onClick={() => openWidget("launcher")}
              aria-label={`Open ${routeConfig.assistantName}`}
              title={routeConfig.assistantName}
              style={ui.fab}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = FAB.ring;
                e.currentTarget.style.background = FAB.hoverBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.background = FAB.bg;
              }}
            >
              Ask{" "}
            </button>{" "}
          </div>{" "}
        </div>
      )}{" "}
      {open && (
        <div style={ui.panel} className="hub-chat-panel">
          {" "}
          <div style={ui.header}>
            {" "}
            <div style={ui.headerLeft}>
              {" "}
              <div style={ui.headerTitleRow}>
                <span style={ui.dot} /> {routeConfig.assistantName}{" "}
              </div>{" "}
              <div style={ui.headerSub}>{routeConfig.helperText}</div>{" "}
            </div>{" "}
            <div style={ui.headerActions}>
              {" "}
              <button style={ui.miniBtn} onClick={clearConversation}>
                Clear{" "}
              </button>{" "}
              <button style={ui.miniBtn} onClick={closeWidget}>
                Minimize{" "}
              </button>{" "}
            </div>{" "}
          </div>{" "}
          <div style={ui.body}>
            {" "}
            <div style={ui.introCard}>
              <div style={ui.introText}>{routeConfig.intro}</div>{" "}
              <div style={ui.helperText}>
                Try one of these common questions:
              </div>{" "}
            </div>{" "}
            <div style={ui.quickSection}>
              <div style={ui.quickLabel}>Suggested questions</div>{" "}
              <div style={ui.quickGrid} className="hub-chat-quick-grid">
                {" "}
                {routeConfig.quickTopics.map((t) => (
                  <button
                    key={t.label}
                    style={ui.quickBtn}
                    onClick={() => fireQuickTopic(t.q, t.label)}
                    disabled={loading}
                    title={t.q}
                  >
                    {t.label}{" "}
                  </button>
                ))}{" "}
              </div>{" "}
            </div>{" "}
            <div style={ui.forumCard}>
              <div style={ui.forumTitle}>Still need help?</div>{" "}
              <div style={ui.forumText}>
                If you can’t find the answer on the website, you can post your
                question in the Hub Discussion Forum. Community members may
                respond, and Hub staff/admins can also help you there.{" "}
              </div>{" "}
              <button
                type="button"
                style={ui.forumBtn}
                onClick={() => {
                  if (user) {
                    navigate("/Chat-Thread");
                  } else {
                    setCreatingThread(true);
                  }
                }}
              >
                Create a Support Thread
              </button>
            </div>{" "}
            {creatingThread && (
              <div style={ui.forumCard}>
                <div style={ui.forumTitle}>Create a Support Thread</div>

                <input
                  style={ui.input}
                  placeholder="Your name"
                  value={guestThreadForm.name}
                  onChange={(e) =>
                    setGuestThreadForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />

                <input
                  style={{ ...ui.input, marginTop: 8 }}
                  placeholder="Your email"
                  value={guestThreadForm.email}
                  onChange={(e) =>
                    setGuestThreadForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />

                <input
                  style={{ ...ui.input, marginTop: 8 }}
                  placeholder="Thread title"
                  value={guestThreadForm.title}
                  onChange={(e) =>
                    setGuestThreadForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />

                <textarea
                  style={{
                    ...ui.input,
                    marginTop: 8,
                    minHeight: 90,
                    resize: "vertical",
                  }}
                  placeholder="Describe your question or issue"
                  value={guestThreadForm.content}
                  onChange={(e) =>
                    setGuestThreadForm((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                />

                <button
                  type="button"
                  style={{
                    ...ui.send,
                    marginTop: 12,
                    width: "100%",
                    opacity: canCreateSupportThread && !loading ? 1 : 0.45,
                    cursor:
                      canCreateSupportThread && !loading
                        ? "pointer"
                        : "not-allowed",
                  }}
                  onClick={createGuestThread}
                  disabled={loading || !canCreateSupportThread}
                >
                  Create Thread
                </button>

                <button
                  type="button"
                  style={{
                    ...ui.miniBtn,
                    marginTop: 8,
                    width: "100%",
                  }}
                  onClick={() => setCreatingThread(false)}
                >
                  Cancel
                </button>
              </div>
            )}
            {messages.slice(1).map((m, i) => (
              <div
                key={`m-${i}`}
                style={m.role === "user" ? ui.bubbleUser : ui.bubbleAsst}
              >
                {" "}
                <div>
                  {" "}
                  {m.role === "assistant" ? (
                    <Linkify options={linkifyOptions}>{m.content}</Linkify>
                  ) : (
                    m.content
                  )}{" "}
                </div>{" "}
                {Array.isArray(m.sources) && m.sources.length > 0 && (
                  <div style={ui.msgMeta}>
                    {" "}
                    {m.sources.map((s, idx) => (
                      <a
                        key={`${s}-${idx}`}
                        href={s}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={ui.sourceBtn}
                        onClick={() =>
                          trackChatEvent("hub_chat_source_clicked", {
                            path: pathname,
                            sourceUrl: s,
                            index: idx,
                          })
                        }
                      >
                        {prettifySourceLabel(s)}{" "}
                      </a>
                    ))}{" "}
                  </div>
                )}{" "}
              </div>
            ))}{" "}
            {loading && <div style={ui.loadingBubble}>Thinking…</div>}
            {error && <div style={ui.errorBubble}>{error}</div>}
            <div ref={endRef} />{" "}
          </div>{" "}
          <div style={ui.footer}>
            {" "}
            <input
              ref={inputRef}
              style={ui.input}
              placeholder="Ask about this page, deadlines, instructions, contacts..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
              aria-label="Chat input"
            />{" "}
            <button style={ui.send} onClick={() => ask()} disabled={loading}>
              Send{" "}
            </button>{" "}
          </div>{" "}
        </div>
      )}{" "}
    </>
  );
}
